/**
 * Services Element Colors Store — user-customisable colors per element type
 *
 * @module core/modules/services/store/element-colors-store
 *
 * @description
 * Zustand store with localStorage persistence. Keeps only user overrides;
 * falls back to SERVICES_ELEMENT_COLORS defaults for untouched entries.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { SERVICES_ELEMENT_COLORS, type ServicesElementId } from '../constants/element-colors';

interface ServicesElementColorsState {
  /** Only colours the user has explicitly changed */
  overrides: Partial<Record<ServicesElementId, string>>;
}

interface ServicesElementColorsActions {
  /** Return override if set, otherwise default */
  getColor: (id: ServicesElementId) => string;
  /** Set a user override */
  setColor: (id: ServicesElementId, hex: string) => void;
  /** Remove a single override (revert to default) */
  resetColor: (id: ServicesElementId) => void;
  /** Remove all overrides */
  resetAll: () => void;
}

export type ServicesElementColorsStore = ServicesElementColorsState & ServicesElementColorsActions;

export const useServicesElementColorsStore = create<ServicesElementColorsStore>()(
  devtools(
    persist(
      (set, get) => ({
        overrides: {},

        getColor(id: ServicesElementId): string {
          return get().overrides[id] ?? SERVICES_ELEMENT_COLORS[id];
        },

        setColor(id: ServicesElementId, hex: string): void {
          set(
            (s) => ({ overrides: { ...s.overrides, [id]: hex } }),
            false,
            'setColor',
          );
        },

        resetColor(id: ServicesElementId): void {
          set(
            (s) => {
              const next = { ...s.overrides };
              delete next[id];
              return { overrides: next };
            },
            false,
            'resetColor',
          );
        },

        resetAll(): void {
          set({ overrides: {} }, false, 'resetAll');
        },
      }),
      { name: 'services-element-colors' },
    ),
    { name: 'ServicesElementColors' },
  ),
);
